import type {
  ExportColumn,
  ExportFormat,
  ExportPrefs,
  ExportSchema,
  ExportSort,
} from "./types";

const STORAGE_PREFIX = "export:v1:";

export function exportPrefsKey(reportType: string): string {
  return `${STORAGE_PREFIX}${reportType}`;
}

export type ExportColumnGroup = {
  /** Undefined for single-table reports. */
  key?: string;
  label?: string;
  columns: ExportColumn[];
};

/** Columns the user can pick for the format, in schema order. */
export function formatColumns(
  schema: ExportSchema,
  format: ExportFormat,
): ExportColumn[] {
  return schema.columns.filter(
    (c) => !c.locked && (!c.formats || c.formats.includes(format)),
  );
}

/** Columns grouped by table when the report has several; one group otherwise. Tables with nothing to pick are left out. */
export function columnGroups(
  schema: ExportSchema,
  format: ExportFormat,
): ExportColumnGroup[] {
  const columns = formatColumns(schema, format);
  const tables = schema.tables ?? [];
  if (tables.length <= 1) return [{ columns }];
  const named = new Set(columns.flatMap((c) => c.tables ?? []));
  const groups = tables
    .map((t) => ({
      key: t.key,
      label: t.label,
      columns: columns.filter((c) => {
        if (c.tables) return c.tables.includes(t.key);
        return named.has(t.key) || !tables.find((x) => x.key === t.key)?.optional;
      }),
    }))
    .filter((g) => {
      if (g.columns.length > 0) return true;
      return !!tables.find((t) => t.key === g.key)?.optional;
    });
  return groups.length > 1 ? groups : [{ columns: groups[0]?.columns ?? [] }];
}

/** Keys in schema order, keeping at most `maxPdfColumns` per table. */
function capPerTable(schema: ExportSchema, keys: string[]): string[] {
  const wanted = new Set(keys);
  const kept = new Set<string>();
  for (const group of columnGroups(schema, "pdf")) {
    const inGroup = group.columns.filter((c) => wanted.has(c.key));
    let count = inGroup.filter((c) => kept.has(c.key)).length;
    for (const c of inGroup) {
      if (kept.has(c.key)) continue;
      if (count >= schema.maxPdfColumns) break;
      kept.add(c.key);
      count += 1;
    }
  }
  return schema.columns.map((c) => c.key).filter((k) => kept.has(k));
}

/** The PDF share of a selection: columns the PDF offers, at most `maxPdfColumns` per table. */
export function pdfColumns(schema: ExportSchema, keys: string[]): string[] {
  return capPerTable(schema, keys);
}

/** True when some table would get more than `maxPdfColumns` columns. */
export function exceedsPdfLimit(schema: ExportSchema, keys: string[]): boolean {
  const set = new Set(keys);
  return columnGroups(schema, "pdf").some(
    (g) =>
      g.columns.filter((c) => set.has(c.key)).length > schema.maxPdfColumns,
  );
}

export function defaultColumns(
  schema: ExportSchema,
  format: ExportFormat,
): string[] {
  const groups = columnGroups(schema, format);
  const keys = groups.flatMap((g) => {
    const defaults = g.columns.filter((c) => c.defaultIn.includes(format));
    return (defaults.length > 0 ? defaults : g.columns).map((c) => c.key);
  });
  const unique = schema.columns
    .map((c) => c.key)
    .filter((k) => keys.includes(k));
  return format === "pdf" ? capPerTable(schema, unique) : unique;
}

export function allColumns(
  schema: ExportSchema,
  format: ExportFormat,
): string[] {
  const all = formatColumns(schema, format).map((c) => c.key);
  return format === "pdf" ? capPerTable(schema, all) : all;
}

/** Columns the backend accepts as sort: `sortable` ones plus the default sort field, one per key. */
export function sortableColumns(schema: ExportSchema): ExportColumn[] {
  const seen = new Set<string>();
  return schema.columns.filter((c) => {
    if (seen.has(c.key)) return false;
    if (c.sortable !== true && c.key !== schema.defaultSort.field) return false;
    seen.add(c.key);
    return true;
  });
}

/** The page's sort when the report can sort by that column, otherwise the report default. */
export function initialExportSort(
  schema: ExportSchema,
  requested: ExportSort | undefined,
): ExportSort {
  if (requested && sortableColumns(schema).some((c) => c.key === requested.field)) {
    return requested;
  }
  return schema.defaultSort;
}

export function defaultExportPrefs(schema: ExportSchema): ExportPrefs {
  return {
    format: schema.formats[0] ?? "xlsx",
    columnsByFormat: {},
  };
}

/**
 * A saved selection from before a table existed has no column in that table.
 * Fill those tables with their defaults so the export stays submittable.
 */
function fillEmptyColumnGroups(
  schema: ExportSchema,
  format: ExportFormat,
  keys: string[],
): string[] {
  const selected = new Set(keys);
  let added = false;
  for (const group of columnGroups(schema, format)) {
    if (group.columns.length === 0) continue;
    if (group.columns.some((column) => selected.has(column.key))) continue;
    const defaults = group.columns.filter((column) => column.defaultIn.includes(format));
    for (const column of defaults.length > 0 ? defaults : group.columns) {
      if (selected.has(column.key)) continue;
      selected.add(column.key);
      added = true;
    }
  }
  if (!added) return keys;
  return schema.columns.map((column) => column.key).filter((key) => selected.has(key));
}

/** Drops anything the current schema no longer supports and falls back to defaults. */
export function sanitizeExportPrefs(
  schema: ExportSchema,
  raw: unknown,
): ExportPrefs {
  const prefs = defaultExportPrefs(schema);
  if (!raw || typeof raw !== "object") return prefs;
  const r = raw as Record<string, unknown>;

  if (
    typeof r["format"] === "string" &&
    schema.formats.includes(r["format"] as ExportFormat)
  ) {
    prefs.format = r["format"] as ExportFormat;
  }

  const byFormat = r["columnsByFormat"];
  if (byFormat && typeof byFormat === "object") {
    for (const format of schema.formats) {
      const known = new Set(formatColumns(schema, format).map((c) => c.key));
      const list = (byFormat as Record<string, unknown>)[format];
      if (!Array.isArray(list)) continue;
      let cols = list.filter(
        (k): k is string => typeof k === "string" && known.has(k),
      );
      if (format === "pdf") cols = capPerTable(schema, cols);
      if (cols.length > 0) {
        cols = fillEmptyColumnGroups(schema, format, cols);
        if (format === "pdf") cols = capPerTable(schema, cols);
      }
      if (cols.length > 0) prefs.columnsByFormat[format] = cols;
    }
  }

  return prefs;
}

export function loadExportPrefs(schema: ExportSchema): ExportPrefs {
  try {
    const raw = window.localStorage.getItem(exportPrefsKey(schema.type));
    return sanitizeExportPrefs(schema, raw ? JSON.parse(raw) : null);
  } catch {
    return defaultExportPrefs(schema);
  }
}

export function saveExportPrefs(reportType: string, prefs: ExportPrefs): void {
  try {
    window.localStorage.setItem(
      exportPrefsKey(reportType),
      JSON.stringify(prefs),
    );
  } catch {
    // Storage full or disabled: preferences are a convenience only.
  }
}
